'use client';

// Global overlay shown during a network switch.
// Mounted at the layout body root (outside all providers); subscribes
// to the transition store and renders only while a switch is in
// progress. Non-dismissable — no close button, no escape, no
// click-outside dismissal. Self-clears after `MIN_TRANSITION_MS`.
//
// Visual modeled after the post-login `auth/callback/_callback-client.tsx`
// "verifying" screen: a centered spinner + a single body line. No card,
// no shadow, no progress bar — just the standard shadcn loading pattern
// the rest of the app uses.

import { useReducedMotion } from 'motion/react';
import type { JSX } from 'react';

import { ShaderSweep } from '@/components/layout/shader-sweep';
import { Icon } from '@/components/ui/icon';
import { Loading03Icon } from '@/components/ui/icon/glyphs';
import type { Network } from '@/lib/network';
import { cn } from '@/lib/ui/utils';
import { useNetworkTransitionStore } from '@/stores/network-transition';

/** User-facing labels — `devnet` is "Demo mode" in production copy
 *  so end users don't see the technical name. `local` only appears
 *  for engineers (Vercel never ships `_LOCAL`). */
function userLabel(network: Network): string {
  if (network === 'mainnet') return 'Mainnet';
  if (network === 'devnet') return 'Demo mode';
  return 'Local';
}

export function NetworkSwitchOverlay(): JSX.Element | null {
  // Hooks must run before the early return. Reduced-motion always wins:
  // the shader sweep only plays when motion is allowed.
  const prefersReducedMotion = useReducedMotion();
  const to = useNetworkTransitionStore((s) => s.to);
  if (to === null) return null;

  const showSweep = prefersReducedMotion !== true;

  return (
    <div
      // `z-modal` (300) sits above `z-sticky` (100, the app header).
      className={cn(
        'fixed inset-0 z-modal flex flex-col items-center justify-center gap-4',
        showSweep ? 'bg-surface-scrim-strong' : 'bg-surface-scrim-default backdrop-blur-sm',
        'animate-in fade-in-0 duration-fast ease-default motion-reduce:animate-none',
      )}
      aria-live="polite"
      aria-busy="true"
      onPointerDown={(e: React.PointerEvent<HTMLDivElement>): void => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {showSweep ? (
        <ShaderSweep className="absolute inset-0" />
      ) : (
        <Icon
          icon={Loading03Icon}
          className="size-10 animate-spin text-white motion-reduce:animate-none"
          aria-hidden
        />
      )}
      {/* `relative` lifts the label into the positioned layer so it paints
          above the absolutely-positioned shader canvas. */}
      <p className={cn('text-body font-medium text-white', showSweep && 'relative')}>
        Switching to {userLabel(to)}…
      </p>
    </div>
  );
}
